'use client'

/**
 * Shared full-screen preview dialog for any URL-addressable artifact.
 *
 * Used by chat artifacts (``agent-artifact-card``). This module is the
 * single home for that shell. Each caller adapts its data shape into
 * {@link MediaPreviewDialogProps} and drops in any extra footer slots.
 *
 * What this dialog handles:
 *   - PreviewBody dispatched by ``pickPreviewKindForAttachment(name, ct)``:
 *     image / video / audio / pdf / spreadsheet / markdown / html / code /
 *     fallback. The first two are full-bleed; ``html`` is full-bleed via
 *     ``<iframe srcDoc>``; everything else flows into a centred padded
 *     scroll wrapper.
 *   - Footer: optional Share, optional extra slot, Copy (smart per-type),
 *     Download.
 *   - Top-right corner, next to Close: a Fullscreen toggle that grows the
 *     dialog to the whole viewport. It is dialog-local state, not the browser
 *     Fullscreen API, so portaled menus such as Export keep working.
 *
 * What this dialog does NOT handle:
 *   - Summary/link-only artifacts that have no URL and no download.
 *     Callers should branch before opening this dialog.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import {
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  FileDownIcon,
  FileIcon,
  Loader2Icon,
  Maximize2Icon,
  Minimize2Icon,
  QuoteIcon,
  RotateCcwIcon,
  Share2Icon,
  XIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { Streamdown } from 'streamdown'
import { useTranslations } from 'next-intl'
import { useSWRConfig } from 'swr'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  HTML_PREVIEW_MAX_BYTES,
  bindFilePreviewTextEviction,
  filePreviewTextKey,
  isNetworkPreviewError,
  useFilePreviewText,
} from '@/hooks/use-file-preview-text'
import {
  ARTIFACT_ELEMENT_QUOTE_MESSAGE,
  ARTIFACT_ELEMENT_QUOTE_MODE_MESSAGE,
  ARTIFACT_ELEMENT_QUOTE_REMOVE_MESSAGE,
  ARTIFACT_ELEMENT_QUOTE_STATE_MESSAGE,
  ARTIFACT_LINK_MESSAGE,
  ARTIFACT_PRINT_DONE_MESSAGE,
  safeArtifactLinkHref,
  wrapHtmlForPreview,
  wrapHtmlForPrint,
  type ArtifactElementQuoteLabels,
} from '@/lib/artifact-html-preview'
import {
  upsertSelectedArtifactElement,
  type SelectedArtifactElement,
} from '@/lib/artifact-element-selection'
import { pdfDownloadFilename } from '@/lib/attachment-action-url'
import { pickPreviewKindForAttachment } from '@/lib/attachment-preview'
import type {
  ArtifactElementQuoteSnapshot,
  MessageArtifactElementQuote,
} from '@/lib/chat-types'
import { copyArtifact } from '@/lib/copy-artifact'
import {
  requestPreviewSourceEviction,
  useHeavyViewerSlot,
} from '@/lib/preview-memory-budget'

const PdfViewer = dynamic(() => import('./pdf-viewer'), {
  ssr: false,
  loading: () => <ViewerLoading />,
})
const SpreadsheetViewer = dynamic(() => import('./spreadsheet-viewer'), {
  ssr: false,
  loading: () => <ViewerLoading />,
})
const DocxViewer = dynamic(() => import('./docx-viewer'), {
  ssr: false,
  loading: () => <ViewerLoading />,
})

const MAX_ARTIFACT_ELEMENT_QUOTE_COUNT = 8
/** Keep in step with ``_ARTIFACT_NOTE_MAX_CHARS`` in ``handoff_metadata.py``. */
const ARTIFACT_ELEMENT_NOTE_MAX_CHARS = 500

export interface MediaPreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** URL to stream / fetch the bytes. Null disables preview + Copy + Download. */
  url: string | null
  /** Display name; also drives extension-based kind inference. */
  name: string
  /** Optional MIME (preferred); falls back to filename extension. */
  contentType?: string | null
  /** Description shown under the title. */
  description?: string
  /** Bytes — appended to description line when > 0. */
  size?: number
  /** Optional alternate URL for Download (e.g. an endpoint that 302s with
   *  ``Content-Disposition: attachment``). Defaults to ``url``. */
  downloadUrl?: string
  /** Optional generated-PDF endpoint for HTML artifacts. */
  htmlPdfUrl?: string
  /** Optional onClick handler for Download — overrides anchor behaviour
   *  (e.g. when the downloadUrl is a signed-redirect endpoint that needs
   *  programmatic anchor click). */
  onDownload?: () => void
  /** Optional Share button. */
  onShare?: () => void
  /** Attachment id used when quoting an element from an HTML artifact. */
  sourceAttachmentId?: string
  /** Optional chat composer hook for quoting an element from an HTML artifact. */
  onArtifactElementQuote?: (quote: MessageArtifactElementQuote) => void
  /** Extra footer content rendered before the built-in buttons. */
  footerExtra?: React.ReactNode
}

export function MediaPreviewDialog({
  open,
  onOpenChange,
  url,
  name,
  contentType,
  description,
  size,
  downloadUrl,
  htmlPdfUrl,
  onDownload,
  onShare,
  sourceAttachmentId,
  onArtifactElementQuote,
  footerExtra,
}: MediaPreviewDialogProps) {
  const ct = (contentType ?? '').toLowerCase()
  const baseType = ct.split(';')[0].trim()
  const kind = pickPreviewKindForAttachment(name, ct)
  const isFullBleed =
    kind === 'html' ||
    kind === 'pdf' ||
    kind === 'spreadsheet' ||
    kind === 'docx'
  const sizeLabel = size && size > 0 ? formatBytes(size) : null
  const descLine = [baseType || null, sizeLabel].filter(Boolean).join(' · ')
  const htmlPdfFilename = useMemo(() => pdfDownloadFilename(name), [name])
  const [elementQuoteMode, setElementQuoteMode] = useState(false)
  const [selectedArtifactElements, setSelectedArtifactElements] = useState<
    SelectedArtifactElement[]
  >([])
  const selectedArtifactElementsRef = useRef<SelectedArtifactElement[]>([])
  const [selectedArtifactHash, setSelectedArtifactHash] = useState<
    string | null
  >(null)
  const htmlDownloadPdfActionRef = useRef<(() => void) | null>(null)
  const [canDownloadHtmlAsPdf, setCanDownloadHtmlAsPdf] = useState(false)
  // Viewport-filling layout, toggled from the footer. Kept as plain dialog
  // state rather than ``element.requestFullscreen()``: Radix portals the
  // Export dropdown (and any toast) to ``document.body``, which sits outside
  // a fullscreened element and would be invisible there.
  const [isFullscreen, setIsFullscreen] = useState(false)
  const tChat = useTranslations('Chat')
  const canQuoteHtml =
    kind === 'html' && Boolean(sourceAttachmentId && onArtifactElementQuote)
  const activeElementQuoteMode =
    open &&
    elementQuoteMode &&
    selectedArtifactElements.length < MAX_ARTIFACT_ELEMENT_QUOTE_COUNT

  const updateSelectedArtifactElements = useCallback(
    (nextElements: SelectedArtifactElement[]) => {
      selectedArtifactElementsRef.current = nextElements
      setSelectedArtifactElements(nextElements)
    },
    [],
  )

  const handleDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setElementQuoteMode(false)
        updateSelectedArtifactElements([])
        setSelectedArtifactHash(null)
        htmlDownloadPdfActionRef.current = null
        setCanDownloadHtmlAsPdf(false)
        setIsFullscreen(false)
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange, updateSelectedArtifactElements],
  )

  /**
   * Take one annotation from the preview, whether it is new or an edit.
   *
   * Editing has to be an update rather than an append, or correcting a note
   * would leave the agent two contradictory instructions for the same element.
   * Two keys decide which row is being written: the iframe's own id, and — for
   * the case where the preview reloaded and started numbering again — the
   * element identity, so re-annotating the same element replaces its row
   * instead of adding a duplicate one.
   */
  const handleArtifactElementUpsert = useCallback(
    (qid: string, quote: MessageArtifactElementQuote) => {
      const element = quote.element ?? quote.elements?.[0]
      if (!element) return
      const { elements: nextElements, rejected } =
        upsertSelectedArtifactElement(
          selectedArtifactElementsRef.current,
          { qid, snapshot: element },
          MAX_ARTIFACT_ELEMENT_QUOTE_COUNT,
        )
      if (rejected) {
        setElementQuoteMode(false)
        toast.info(
          tChat('quoteElementMaxReached', {
            count: MAX_ARTIFACT_ELEMENT_QUOTE_COUNT,
          }),
        )
        return
      }

      setSelectedArtifactHash(quote.artifact_hash ?? null)
      updateSelectedArtifactElements(nextElements)
      if (nextElements.length >= MAX_ARTIFACT_ELEMENT_QUOTE_COUNT) {
        setElementQuoteMode(false)
      }
    },
    [tChat, updateSelectedArtifactElements],
  )

  /** Deleted from its own bubble inside the preview. */
  const handleArtifactElementDiscard = useCallback(
    (qid: string) => {
      updateSelectedArtifactElements(
        selectedArtifactElementsRef.current.filter(item => item.qid !== qid),
      )
    },
    [updateSelectedArtifactElements],
  )

  /** Deleted from its card in the tray below; the pin follows via the id list. */
  const handleRemoveArtifactElement = useCallback(
    (index: number) => {
      updateSelectedArtifactElements(
        selectedArtifactElementsRef.current.filter(
          (_, itemIndex) => itemIndex !== index,
        ),
      )
    },
    [updateSelectedArtifactElements],
  )

  const handleConfirmArtifactElements = useCallback(() => {
    if (
      !sourceAttachmentId ||
      !onArtifactElementQuote ||
      selectedArtifactElements.length === 0
    ) {
      return
    }
    const snapshots = selectedArtifactElements.map(item => item.snapshot)
    onArtifactElementQuote({
      kind: 'artifact_element',
      text: buildArtifactElementsQuoteText(
        name,
        snapshots,
        tChat('quoteElementTargetsSummary', {
          count: snapshots.length,
        }),
      ),
      source_attachment_id: sourceAttachmentId,
      artifact_name: name,
      artifact_hash: selectedArtifactHash,
      element: snapshots[0],
      elements: snapshots,
    })
    handleDialogOpenChange(false)
  }, [
    handleDialogOpenChange,
    name,
    onArtifactElementQuote,
    selectedArtifactElements,
    selectedArtifactHash,
    sourceAttachmentId,
    tChat,
  ])

  const handleClearArtifactElements = useCallback(() => {
    updateSelectedArtifactElements([])
    setSelectedArtifactHash(null)
  }, [updateSelectedArtifactElements])

  const handleToggleElementQuoteMode = useCallback(() => {
    if (
      !elementQuoteMode &&
      selectedArtifactElements.length >= MAX_ARTIFACT_ELEMENT_QUOTE_COUNT
    ) {
      toast.info(
        tChat('quoteElementMaxReached', {
          count: MAX_ARTIFACT_ELEMENT_QUOTE_COUNT,
        }),
      )
      return
    }
    setElementQuoteMode(value => !value)
  }, [elementQuoteMode, selectedArtifactElements.length, tChat])

  const handleHtmlDownloadPdfActionChange = useCallback(
    (action: (() => void) | null) => {
      htmlDownloadPdfActionRef.current = action
      setCanDownloadHtmlAsPdf(Boolean(action))
    },
    [],
  )

  const handleDownloadHtmlAsPdf = useCallback(() => {
    htmlDownloadPdfActionRef.current?.()
  }, [])

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(value => !value)
  }, [])

  const selectedArtifactElementCount = selectedArtifactElements.length
  const retainedQuoteIds = useMemo(
    () => selectedArtifactElements.map(item => item.qid),
    [selectedArtifactElements],
  )

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        showCloseButton
        className={cn(
          'flex flex-col gap-0 overflow-hidden p-0',
          isFullscreen
            ? 'h-dvh max-h-dvh w-screen !max-w-none rounded-none'
            : 'h-[85vh] max-h-[85vh] w-[90vw] !max-w-5xl',
        )}
      >
        {/* Right padding clears the two corner buttons (fullscreen + close). */}
        <DialogHeader className="shrink-0 border-b py-3 pe-24 ps-5 text-start">
          <DialogTitle className="truncate text-sm font-medium">
            {name}
          </DialogTitle>
          {descLine ? (
            <DialogDescription className="text-muted-foreground truncate text-xs">
              {descLine}
            </DialogDescription>
          ) : null}
          {description ? (
            <p className="text-muted-foreground/90 mt-1 text-xs leading-snug">
              {description}
            </p>
          ) : null}
        </DialogHeader>

        {isFullBleed ? (
          <div className="flex-1 overflow-hidden">
            <PreviewBody
              url={url}
              name={name}
              kind={kind}
              fallbackLabel={baseType || name}
              htmlQuote={
                canQuoteHtml && sourceAttachmentId
                  ? {
                      sourceAttachmentId,
                      active: activeElementQuoteMode,
                      onQuote: handleArtifactElementUpsert,
                      onDiscardQuote: handleArtifactElementDiscard,
                      retainedQuoteIds,
                    }
                  : undefined
              }
              onHtmlDownloadPdfActionChange={handleHtmlDownloadPdfActionChange}
            />
          </div>
        ) : (
          <div className="bg-muted/30 flex-1 overflow-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <PreviewBody
                url={url}
                name={name}
                kind={kind}
                fallbackLabel={baseType || name}
                htmlQuote={
                  canQuoteHtml && sourceAttachmentId
                    ? {
                        sourceAttachmentId,
                        active: activeElementQuoteMode,
                        onQuote: handleArtifactElementUpsert,
                        onDiscardQuote: handleArtifactElementDiscard,
                        retainedQuoteIds,
                      }
                    : undefined
                }
                onHtmlDownloadPdfActionChange={
                  handleHtmlDownloadPdfActionChange
                }
              />
            </div>
          </div>
        )}

        {canQuoteHtml && selectedArtifactElementCount > 0 ? (
          <ArtifactElementSelectionTray
            elements={selectedArtifactElements}
            onRemove={handleRemoveArtifactElement}
            onClear={handleClearArtifactElements}
          />
        ) : null}

        {onShare || footerExtra || url ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t px-5 py-2">
            {footerExtra}
            {canQuoteHtml ? (
              <Button
                variant={activeElementQuoteMode ? 'secondary' : 'ghost'}
                size="sm"
                onClick={handleToggleElementQuoteMode}
                className="gap-1.5"
              >
                <QuoteIcon className="size-3.5" />
                {tChat('quoteElement')}
              </Button>
            ) : null}
            {canQuoteHtml && selectedArtifactElementCount > 0 ? (
              <Button
                variant="default"
                size="sm"
                onClick={handleConfirmArtifactElements}
                className="gap-1.5"
              >
                <QuoteIcon className="size-3.5" />
                {tChat('quoteElementConfirm')}
              </Button>
            ) : null}
            {onShare ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onShare}
                className="gap-1.5"
              >
                <Share2Icon className="size-3.5" />
                <ShareLabel />
              </Button>
            ) : null}
            {url ? (
              <CopyButton url={url} name={name} contentType={contentType} />
            ) : null}
            {kind === 'html' && url ? (
              <DownloadAsPdfButton
                href={htmlPdfUrl}
                filename={htmlPdfFilename}
                onClick={handleDownloadHtmlAsPdf}
                printReady={canDownloadHtmlAsPdf}
              />
            ) : null}
            {url ? (
              <DownloadButton
                href={downloadUrl ?? url}
                filename={name}
                onClick={onDownload}
              />
            ) : null}
          </div>
        ) : null}

        {/*
          Sits next to the close button `DialogContent` renders at
          `top-4 end-4`; rendered last so the two corner controls are
          adjacent in tab order too.
        */}
        <FullscreenToggleButton
          isFullscreen={isFullscreen}
          onToggle={handleToggleFullscreen}
        />
      </DialogContent>
    </Dialog>
  )
}

function DownloadAsPdfButton({
  href,
  filename,
  onClick,
  printReady,
}: {
  href?: string
  filename: string
  onClick: () => void
  /** Whether the in-browser print-to-PDF fallback is ready (the HTML preview
   *  has loaded). Only consulted when no backend PDF ``href`` is available. */
  printReady: boolean
}) {
  const t = useTranslations('Chat.artifact')
  const [isDownloading, setIsDownloading] = useState(false)

  const handleBackendPdfDownload = async () => {
    if (!href || isDownloading) return
    setIsDownloading(true)
    try {
      const response = await fetch(href)
      if (!response.ok) {
        throw new Error(`PDF export failed with status ${response.status}`)
      }
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      try {
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = filename
        anchor.rel = 'noopener noreferrer'
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
      }
    } catch (err) {
      console.error('[preview] download PDF failed', err)
      toast.error(t('downloadPdfFailed'))
    } finally {
      setIsDownloading(false)
    }
  }

  if (href) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBackendPdfDownload}
        disabled={isDownloading}
        aria-busy={isDownloading}
        className="gap-1.5"
      >
        {isDownloading ? (
          <Loader2Icon className="size-3.5 animate-spin" />
        ) : (
          <FileDownIcon className="size-3.5" />
        )}
        {t('downloadAsPdf')}
      </Button>
    )
  }
  // No backend PDF endpoint for this artifact (e.g. the landing report, served
  // by turn id with no attachment row). Fall back to the in-browser print-to-
  // PDF path. Keep the button live rather than silently disabled: if the print
  // path is not ready yet (preview still loading / failed to load), tell the
  // user why instead of swallowing the click.
  const handleClick = () => {
    if (printReady) {
      onClick()
      return
    }
    toast.info(t('downloadPdfNotReady'))
  }
  return (
    <Button variant="ghost" size="sm" onClick={handleClick} className="gap-1.5">
      <FileDownIcon className="size-3.5" />
      {t('downloadAsPdf')}
    </Button>
  )
}

/**
 * The list of what has been annotated so far, under the preview.
 *
 * The notes themselves are written in the preview, next to the elements they
 * are about; this is the receipt — it says what is about to be sent, in the
 * order the agent will read it, and it is where a target gets dropped.
 */
function ArtifactElementSelectionTray({
  elements,
  onRemove,
  onClear,
}: {
  elements: SelectedArtifactElement[]
  onRemove: (index: number) => void
  onClear: () => void
}) {
  const tChat = useTranslations('Chat')
  return (
    <div className="bg-background/95 shrink-0 border-t px-5 py-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {tChat('quoteElementSelectedCount', { count: elements.length })}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClear}>
          {tChat('quoteElementClear')}
        </Button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {elements.map(({ qid, snapshot }, index) => (
          <div
            key={qid}
            className="border-border bg-muted/30 flex w-64 shrink-0 items-start gap-2 rounded-md border p-2"
          >
            <div className="bg-primary text-primary-foreground flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium">
              {index + 1}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="bg-background text-foreground rounded border px-1.5 py-0.5 font-mono text-[11px]">
                  &lt;{snapshot.tag}&gt;
                </span>
                {snapshot.role ? (
                  <span className="text-muted-foreground truncate text-[11px]">
                    {snapshot.role}
                  </span>
                ) : null}
              </div>
              {/*
                The note leads, because it is the instruction — the element it
                is attached to is the address, and reads underneath.
              */}
              {snapshot.note ? (
                <p className="mt-1 line-clamp-2 text-xs font-medium">
                  {snapshot.note}
                </p>
              ) : (
                <p className="text-muted-foreground/70 mt-1 truncate text-xs italic">
                  {tChat('quoteElementNoteEmpty')}
                </p>
              )}
              <p className="text-muted-foreground mt-0.5 truncate text-[11px]">
                {artifactElementDisplayText(snapshot)}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0"
              onClick={() => onRemove(index)}
              aria-label={tChat('quoteElementRemove', { index: index + 1 })}
            >
              <XIcon className="size-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

function ShareLabel() {
  const t = useTranslations('Share')
  return <>{t('shareButton')}</>
}

function DownloadButton({
  href,
  filename,
  onClick,
}: {
  href: string
  filename: string
  onClick?: () => void
}) {
  const t = useTranslations('Chat.artifact')
  if (onClick) {
    return (
      <Button variant="ghost" size="sm" onClick={onClick} className="gap-1.5">
        <DownloadIcon className="size-3.5" />
        {t('download')}
      </Button>
    )
  }
  return (
    <Button asChild variant="ghost" size="sm">
      <a href={href} download={filename} className="gap-1.5">
        <DownloadIcon className="size-3.5" />
        {t('download')}
      </a>
    </Button>
  )
}

function FullscreenToggleButton({
  isFullscreen,
  onToggle,
}: {
  isFullscreen: boolean
  onToggle: () => void
}) {
  const t = useTranslations('Chat.artifact')
  const label = isFullscreen ? t('exitFullscreen') : t('fullscreen')
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={onToggle}
      aria-pressed={isFullscreen}
      aria-label={label}
      title={label}
      className="absolute top-4 end-14"
    >
      {isFullscreen ? <Minimize2Icon /> : <Maximize2Icon />}
    </Button>
  )
}

function CopyButton({
  url,
  name,
  contentType,
}: {
  url: string
  name: string
  contentType?: string | null
}) {
  const t = useTranslations('Chat.artifact')
  const [state, setState] = useState<'idle' | 'copying' | 'copied'>('idle')

  const handleCopy = async () => {
    if (state === 'copying') return
    setState('copying')
    try {
      await copyArtifact({ url, name, contentType: contentType ?? '' })
      setState('copied')
      setTimeout(() => setState('idle'), 1500)
    } catch (err) {
      setState('idle')
      console.error('[preview] copy failed', err)
      toast.error(t('copyFailed'))
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      disabled={state === 'copying'}
      className="gap-1.5"
    >
      {state === 'copied' ? (
        <CheckIcon className="size-3.5" />
      ) : state === 'copying' ? (
        <Loader2Icon className="size-3.5 animate-spin" />
      ) : (
        <CopyIcon className="size-3.5" />
      )}
      {state === 'copied' ? t('copied') : t('copy')}
    </Button>
  )
}

function PreviewBody({
  url,
  name,
  kind,
  fallbackLabel,
  htmlQuote,
  onHtmlDownloadPdfActionChange,
}: {
  url: string | null
  name: string
  kind: ReturnType<typeof pickPreviewKindForAttachment>
  fallbackLabel: string
  htmlQuote?: HtmlQuoteController
  onHtmlDownloadPdfActionChange?: (action: (() => void) | null) => void
}) {
  if (!url) return <FallbackBody label={fallbackLabel} />
  switch (kind) {
    // Keyed by ``url`` so the per-element error state resets cleanly when the
    // source changes, mirroring the key-boundary idiom used by the viewers.
    case 'image':
      return <ImageBody key={url} url={url} name={name} />
    case 'video':
      return <VideoBody key={url} url={url} />
    case 'audio':
      return <AudioBody key={url} url={url} />
    case 'pdf':
      return <PdfViewer src={url} mode="full" />
    case 'spreadsheet':
      return <SpreadsheetViewer src={url} mode="full" />
    case 'docx':
      return <DocxViewer src={url} mode="full" />
    case 'markdown':
      return <MarkdownBody url={url} />
    case 'html':
      return (
        <HtmlBody
          url={url}
          title={name}
          htmlQuote={htmlQuote}
          onDownloadPdfActionChange={onHtmlDownloadPdfActionChange}
        />
      )
    case 'code':
      return <CodeBody url={url} />
    default:
      return <FallbackBody label={fallbackLabel} />
  }
}

// Native media elements (image/video/audio) stream their own bytes, but unlike
// the text-based viewers they have no built-in failure surface: a 404 from the
// serve proxy or an expired signed URL would otherwise leave the browser's
// broken-media glyph with no recovery hint. Each wrapper swaps in the shared
// ``ErrorBody`` ("Cannot load preview — use Open in new tab", plus a retry
// button that remounts the element) on ``error``. Native media elements do
// not expose HTTP status, so network and 404 failures share the generic copy.
//
// The height cap on image/video uses the dialog's own ``85vh`` basis (minus
// header / footer / padding overhead) rather than ``max-h-full``: the centering
// wrapper has an auto height that grows to fit the media, so a percentage cap
// resolves to no constraint and tall portrait media overflow into a scroll. A
// viewport-relative cap keeps them fully visible while still filling the
// available height on large screens.

function ImageBody({ url, name }: { url: string; name: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) return <ErrorBody onRetry={() => setFailed(false)} />
  return (
    <img
      src={url}
      alt={name}
      onError={() => setFailed(true)}
      className="max-h-[calc(85vh-9rem)] max-w-full rounded object-contain"
    />
  )
}

function VideoBody({ url }: { url: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) return <ErrorBody onRetry={() => setFailed(false)} />
  return (
    <video
      src={url}
      controls
      playsInline
      onError={() => setFailed(true)}
      className="max-h-[calc(85vh-9rem)] max-w-full"
    />
  )
}

function AudioBody({ url }: { url: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) return <ErrorBody onRetry={() => setFailed(false)} />
  return (
    <audio
      src={url}
      controls
      onError={() => setFailed(true)}
      className="w-full max-w-lg"
    />
  )
}

function MarkdownBody({ url }: { url: string }) {
  // `required`: the user clicked to open this. Deferring a user-initiated
  // preview because thumbnails filled the budget would be indefensible.
  const { data, isLoading, error, retry } = useFilePreviewText(url, undefined, {
    required: true,
  })
  // Error branch comes first: SWR flips isLoading back to true while an
  // auto-retry is in flight, and error-first ordering keeps the error
  // page (with its retry button) up instead of flickering to a spinner.
  if (error) {
    return <ErrorBody network={isNetworkPreviewError(error)} onRetry={retry} />
  }
  if (isLoading) return <ViewerLoading />
  if (data == null) return <ErrorBody onRetry={retry} />
  return (
    <div className="prose prose-sm dark:prose-invert bg-background w-full max-w-3xl rounded p-4">
      <Streamdown>{data}</Streamdown>
    </div>
  )
}

function CodeBody({ url }: { url: string }) {
  // `required` — see MarkdownBody.
  const { data, isLoading, error, retry } = useFilePreviewText(url, undefined, {
    required: true,
  })
  // Error branch first — see MarkdownBody for why.
  if (error) {
    return <ErrorBody network={isNetworkPreviewError(error)} onRetry={retry} />
  }
  if (isLoading) return <ViewerLoading />
  if (data == null) return <ErrorBody onRetry={retry} />
  return (
    <pre className="bg-background w-full max-w-4xl rounded p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap">
      {data}
    </pre>
  )
}

interface HtmlQuoteController {
  sourceAttachmentId: string
  /** Annotating is on: hovering highlights, clicking opens a note bubble. */
  active: boolean
  /** One note arrived from the preview — new, or an edit to ``qid``. */
  onQuote: (qid: string, quote: MessageArtifactElementQuote) => void
  /** A note was deleted from inside its own bubble. */
  onDiscardQuote: (qid: string) => void
  /**
   * The notes the dialog still holds, in card order. Mirrored into the preview
   * so its pins match the cards: one removed here loses its pin, and the rest
   * renumber.
   */
  retainedQuoteIds: readonly string[]
}

function HtmlBody({
  url,
  title,
  htmlQuote,
  onDownloadPdfActionChange,
}: {
  url: string
  title: string
  htmlQuote?: HtmlQuoteController
  onDownloadPdfActionChange?: (action: (() => void) | null) => void
}) {
  // Fetch HTML as text and render via ``srcDoc``: this keeps the iframe at
  // ``about:srcdoc`` so agent-shipped scripts cannot read the (possibly
  // signed) source URL via ``window.location``. Sandbox ``allow-scripts``
  // with no ``allow-same-origin`` keeps the iframe in an opaque origin.
  const { data, isLoading, error, retry } = useFilePreviewText(
    url,
    HTML_PREVIEW_MAX_BYTES,
    { required: true },
  )
  const { mutate, cache } = useSWRConfig()
  // This viewer holds a decoded HTML document in an iframe — the heaviest of
  // the preview surfaces. It is user-initiated, so it is always admitted, but
  // it still occupies a budget slot: while the dialog is open, artifact
  // thumbnails over the cap degrade to static tiles.
  // No visibility ref needed: a `required` consumer is admitted unconditionally,
  // so it never competes for a slot — it only occupies one.
  useHeavyViewerSlot(true)
  // Release the up-to-25 MB source string when this preview unmounts — dialog
  // close (Radix unmounts after the exit animation, so the iframe never
  // flashes an empty state), route-change unmount that skips
  // onOpenChange(false), or url switch (cleanup runs with the old url).
  //
  // The request goes through the preview memory budget rather than calling
  // ``mutate`` here, because the registry is the single evictor of record: it
  // must drop the entry from its byte account in the same step, otherwise it
  // would keep believing these 25 MB are resident and evict live entries
  // prematurely. Unpinned → evicted now; still pinned by another consumer →
  // evicted the moment the last one releases, so the eviction is never lost
  // and a visible tile is never blanked. HTML-only — markdown/code entries
  // stay under the 2 MB cap and are cheap to keep.
  //
  // `cache` rides along because releasing the string is only half of an
  // eviction: the key's SWR request markers have to go with it, or the next
  // open inside `dedupingInterval` dedupes onto the dead request and settles
  // with no data and no error (the stg "second open shows Cannot load
  // preview"). `bindFilePreviewTextEviction` owns both halves.
  useEffect(() => {
    const key = filePreviewTextKey(url, HTML_PREVIEW_MAX_BYTES)
    return () => {
      requestPreviewSourceEviction(
        key,
        bindFilePreviewTextEviction(mutate, key, cache),
      )
    }
  }, [mutate, cache, url])
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const printFrameRef = useRef<HTMLIFrameElement | null>(null)
  const printCleanupTimerRef = useRef<number | null>(null)
  const tChat = useTranslations('Chat')
  // Copy for the note bubble the preview draws inside itself, memoised because
  // it feeds ``srcDoc``: a fresh object per render would rebuild the whole
  // document and throw away the reader's scroll position.
  const quoteLabels = useMemo<ArtifactElementQuoteLabels>(
    () => ({
      placeholder: tChat('quoteElementNotePlaceholder'),
      add: tChat('quoteElementNoteAdd'),
      save: tChat('quoteElementNoteSave'),
      cancel: tChat('quoteElementNoteCancel'),
      remove: tChat('quoteElementNoteRemove'),
      hint: tChat('quoteElementNoteHint'),
    }),
    [tChat],
  )
  // Present only where annotating is offered at all — the share viewer and the
  // settings list pass no controller, and get a preview with no tooling in it.
  const elementQuoteLabels = htmlQuote ? quoteLabels : undefined
  const reportFontBase =
    typeof window === 'undefined' ? undefined : window.location.origin
  const srcDoc = useMemo(
    () =>
      data == null
        ? undefined
        : wrapHtmlForPreview(data, { elementQuoteLabels, reportFontBase }),
    [data, elementQuoteLabels, reportFontBase],
  )
  // Separate wrap for the print frame: same link interceptor + ``<base>`` as the
  // preview (so the printout matches the screen) plus a self-triggering
  // ``print()`` snippet. Element-quote tooling is irrelevant when printing.
  const printSrcDoc = useMemo(
    () =>
      data == null
        ? undefined
        : wrapHtmlForPrint(data, { reportFontBase }),
    [data, reportFontBase],
  )
  const artifactHash = useMemo(
    () => (data == null ? null : stableHash(data)),
    [data],
  )

  const cleanupPrintFrame = useCallback(() => {
    if (printCleanupTimerRef.current != null) {
      window.clearTimeout(printCleanupTimerRef.current)
      printCleanupTimerRef.current = null
    }
    printFrameRef.current?.remove()
    printFrameRef.current = null
  }, [])

  const printHtmlAsPdf = useCallback(() => {
    if (!printSrcDoc) return
    cleanupPrintFrame()

    const frame = document.createElement('iframe')
    frame.title = `${title} PDF print preview`
    frame.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
    frame.setAttribute('sandbox', 'allow-scripts allow-modals')
    frame.setAttribute('referrerpolicy', 'no-referrer')

    // The print is triggered from INSIDE the frame (see ``wrapHtmlForPrint``):
    // the opaque-origin sandbox blocks the parent from calling
    // ``frame.contentWindow.print()`` cross-origin, so the frame prints itself
    // on load and posts ``ARTIFACT_PRINT_DONE_MESSAGE`` back on ``afterprint``.
    // The backstop timer is armed on the frame's ``load`` (the iframe element's
    // event is readable cross-origin) — NOT at click time — so a slow-loading
    // artifact does not get its print cancelled before it starts. It covers the
    // case where ``afterprint`` never arrives (user dismisses the dialog, or the
    // browser suppresses the event).
    frame.onload = () => {
      if (printFrameRef.current !== frame) return
      printCleanupTimerRef.current = window.setTimeout(() => {
        if (printFrameRef.current === frame) cleanupPrintFrame()
      }, 60_000)
    }
    document.body.appendChild(frame)
    printFrameRef.current = frame
    frame.srcdoc = printSrcDoc
  }, [cleanupPrintFrame, printSrcDoc, title])

  // Remove the print frame once it relays ``afterprint``. The message must come
  // from the print frame itself (not the visible preview iframe).
  useEffect(() => {
    function onPrintDone(e: MessageEvent) {
      const frame = printFrameRef.current
      if (!frame || e.source !== frame.contentWindow) return
      const payload = e.data as { type?: unknown } | null
      if (payload?.type === ARTIFACT_PRINT_DONE_MESSAGE) cleanupPrintFrame()
    }
    window.addEventListener('message', onPrintDone)
    return () => window.removeEventListener('message', onPrintDone)
  }, [cleanupPrintFrame])

  useEffect(() => {
    if (!printSrcDoc) {
      onDownloadPdfActionChange?.(null)
      return
    }
    onDownloadPdfActionChange?.(printHtmlAsPdf)
    return () => onDownloadPdfActionChange?.(null)
  }, [onDownloadPdfActionChange, printHtmlAsPdf, printSrcDoc])

  useEffect(() => cleanupPrintFrame, [cleanupPrintFrame])

  const postQuoteMode = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: ARTIFACT_ELEMENT_QUOTE_MODE_MESSAGE,
        active: Boolean(htmlQuote?.active),
      },
      '*',
    )
  }, [htmlQuote?.active])

  const retainedQuoteIds = htmlQuote?.retainedQuoteIds
  // Tell the preview which notes survived, so a card removed in the tray takes
  // its pin with it and the remaining pins renumber to match.
  const postQuoteState = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: ARTIFACT_ELEMENT_QUOTE_STATE_MESSAGE,
        qids: retainedQuoteIds ? [...retainedQuoteIds] : [],
      },
      '*',
    )
  }, [retainedQuoteIds])

  const postQuoteHandshake = useCallback(() => {
    postQuoteMode()
    postQuoteState()
  }, [postQuoteMode, postQuoteState])

  useEffect(() => {
    postQuoteHandshake()
  }, [postQuoteHandshake, srcDoc])

  // External links inside the sandboxed (popup-less) iframe are relayed here
  // and opened by the parent. Verify the message came from this iframe and
  // only open http(s) URLs so a hostile artifact cannot trigger arbitrary
  // navigations.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) {
        return
      }
      const payload = e.data as {
        type?: unknown
        href?: unknown
        qid?: unknown
        element?: unknown
      } | null
      if (!payload) return
      if (payload.type === ARTIFACT_LINK_MESSAGE) {
        const href = safeArtifactLinkHref(payload.href)
        if (href) window.open(href, '_blank', 'noopener,noreferrer')
        return
      }
      // Deliberately not gated on ``active``: reaching the cap turns picking
      // off, and rewording or deleting a note already made has to keep working
      // after that — otherwise the eighth annotation freezes the other seven.
      if (payload.type === ARTIFACT_ELEMENT_QUOTE_MESSAGE && htmlQuote) {
        const qid = stringValue(payload.qid, 64)
        const element = normalizeArtifactElementQuote(payload.element)
        if (!qid || !element) return
        htmlQuote.onQuote(qid, {
          kind: 'artifact_element',
          text: buildArtifactElementQuoteText(title, element),
          source_attachment_id: htmlQuote.sourceAttachmentId,
          artifact_name: title,
          artifact_hash: artifactHash,
          element,
        })
        return
      }
      if (payload.type === ARTIFACT_ELEMENT_QUOTE_REMOVE_MESSAGE && htmlQuote) {
        const qid = stringValue(payload.qid, 64)
        if (qid) htmlQuote.onDiscardQuote(qid)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [artifactHash, htmlQuote, title])

  // Error branch first — see MarkdownBody for why.
  if (error) {
    return <ErrorBody network={isNetworkPreviewError(error)} onRetry={retry} />
  }
  if (isLoading) return <ViewerLoading />
  if (srcDoc == null) return <ErrorBody onRetry={retry} />
  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcDoc}
      title={title}
      className="size-full border-0 bg-white"
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      onLoad={postQuoteHandshake}
    />
  )
}

function normalizeArtifactElementQuote(
  value: unknown,
): ArtifactElementQuoteSnapshot | null {
  if (!isRecord(value)) return null
  const tag = stringValue(value.tag, 40)
  const domPath = stringValue(value.dom_path, 500)
  if (!tag || !domPath) return null
  const selectorHints = Array.isArray(value.selector_hints)
    ? value.selector_hints
        .map(item => stringValue(item, 300))
        .filter((item): item is string => Boolean(item))
        .slice(0, 6)
    : []
  return {
    tag,
    role: nullableStringValue(value.role, 80),
    label: nullableStringValue(value.label, 200),
    text: nullableStringValue(value.text, 1000),
    note: nullableStringValue(value.note, ARTIFACT_ELEMENT_NOTE_MAX_CHARS),
    html_excerpt: nullableStringValue(value.html_excerpt, 2000),
    selector_hints: selectorHints,
    dom_path: domPath,
    text_hash: nullableStringValue(value.text_hash, 80),
    outer_html_hash: nullableStringValue(value.outer_html_hash, 80),
    rect: normalizeRect(value.rect),
  }
}

function normalizeRect(value: unknown): ArtifactElementQuoteSnapshot['rect'] {
  if (!isRecord(value)) return null
  const x = numberValue(value.x)
  const y = numberValue(value.y)
  const width = numberValue(value.width)
  const height = numberValue(value.height)
  if (x == null || y == null || width == null || height == null) return null
  return { x, y, width, height }
}

function buildArtifactElementQuoteText(
  artifactName: string,
  element: ArtifactElementQuoteSnapshot,
): string {
  const label =
    element.label ||
    element.text ||
    element.selector_hints[0] ||
    element.dom_path ||
    ''
  const suffix = label ? ` "${truncateForLabel(label, 80)}"` : ''
  return `${artifactName} · <${element.tag}>${suffix}`
}

function buildArtifactElementsQuoteText(
  artifactName: string,
  elements: ArtifactElementQuoteSnapshot[],
  targetsSummary: string,
): string {
  if (elements.length === 1) {
    return buildArtifactElementQuoteText(artifactName, elements[0])
  }
  return `${artifactName} · ${targetsSummary}`
}

function artifactElementDisplayText(
  element: ArtifactElementQuoteSnapshot,
): string {
  return truncateForLabel(
    element.label ||
      element.text ||
      element.selector_hints[0] ||
      element.dom_path ||
      `<${element.tag}>`,
    90,
  )
}

function truncateForLabel(value: string, maxLength: number): string {
  const normalized = value.replace(/\s+/g, ' ').trim()
  return normalized.length > maxLength
    ? `${normalized.slice(0, maxLength - 3)}...`
    : normalized
}

function stableHash(value: string): string {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (Math.imul(31, hash) + value.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(16)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function stringValue(value: unknown, maxLength: number): string | null {
  if (typeof value !== 'string') return null
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (!normalized) return null
  return normalized.length > maxLength
    ? normalized.slice(0, maxLength)
    : normalized
}

function nullableStringValue(value: unknown, maxLength: number): string | null {
  return stringValue(value, maxLength)
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function ViewerLoading() {
  return (
    <div className="text-muted-foreground/60 flex h-full w-full items-center justify-center">
      <Loader2Icon className="size-5 animate-spin" />
    </div>
  )
}

function ErrorBody({
  network,
  onRetry,
}: {
  network?: boolean
  onRetry?: () => void
}) {
  const t = useTranslations('Chat.artifact')
  return (
    <div className="text-muted-foreground flex flex-col items-center gap-2 text-sm">
      <FileIcon className="size-12" />
      {network ? (
        <>
          <p>{t('networkError')}</p>
          <p className="text-xs">{t('networkErrorHint')}</p>
        </>
      ) : (
        <>
          <p>{t('cannotLoadPreview')}</p>
          <p className="text-xs">{t('useDownloadInstead')}</p>
        </>
      )}
      {onRetry ? (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          <RotateCcwIcon className="size-4" />
          {t('retry')}
        </Button>
      ) : null}
    </div>
  )
}

function FallbackBody({ label }: { label: string }) {
  const t = useTranslations('Chat.artifact')
  return (
    <div className="text-muted-foreground flex flex-col items-center gap-2 text-sm">
      <FileIcon className="size-12" />
      <p className="text-xs">{t('noInlinePreview', { contentType: label })}</p>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
